import React from "react";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import moment from "moment";
import Moment from "react-moment";
import Avatar from "components/Avatar";
import { Link } from "react-router-dom";
import { Dropdown, OverlayTrigger, Popover } from "react-bootstrap";
import {
	FiCalendar,
	FiMessageSquare,
	FiMoreHorizontal,
	FiPaperclip,
} from "react-icons/fi";
import { MdAttachFile } from "react-icons/md";
import EmployeeListItemHeader from "components/Employees/EmployeeListItemHeader";
import { EmployeeLink } from "components/Employees/EmployeeEditDrawer";
import AbsenceListItemPeriod from "components/Absences/AbsenceListItemPeriod";
import DataTable from "components/DataTable";
import ShiftPill from "components/Shifts/ShiftPill";
import { wfStatus } from "services/workflowsService";
import { workflowsService } from "services";
import { getDiffDays } from "utils/datetime";

const shiftPillColor = (shift) =>
	shift.background_color || shift.border_color || "";

const getStatusPillClass = (status) => {
	switch (status) {
		case wfStatus.approved:
			return "estado-si";
		case wfStatus.denied:
			return "estado-no";
		case wfStatus.canceled:
			return "estado-anulado";
		case wfStatus.pending:
			return "estado-pendiente";
		default:
			return "estado-no";
	}
};

const getPeriodLabel = (absence) => {
	const start = moment(absence.start);
	const end = absence.end ? moment(absence.end) : null;
	const startLabel = start.format("D MMM");

	if (!end || start.isSame(end, "day")) return startLabel;
	return `${startLabel} - ${end.format("D MMM")}`;
};

const getDurationLabel = (absence) => {
	if (absence.workflow_hourly_requested) {
		return `${String(absence.duration || 0).replace(".", ",")} h`;
	}

	let days = absence.effective_days || 0;
	if (days === 0) days = getDiffDays(absence.start, absence.end);
	return `${days} d`;
};

const getAbsenceCode = (absence) => {
	const shiftName =
		(absence.shift && absence.shift.name) || absence.shift_type || "";
	const words = shiftName.match(/[\p{L}\p{N}]+/gu) || [];
	return words
		.slice(0, 3)
		.map((word) => word.charAt(0))
		.join("")
		.toUpperCase();
};

const getTypeBackground = (color) => {
	if (/^#[0-9a-f]{6}$/i.test(color)) return `${color}18`;
	return "#f4f2ff";
};

const renderPersonWithDate = ({ person, date, linkable = true }) => {
	if (!person) return null;

	const fullName = `${person.name} ${person.surname}`.trim();
	const profilePath = `/employees/${person.id}`;

	return (
		<div className="td-name-content">
			{linkable ? (
				<Link to={profilePath}>
					<Avatar userId={person.id} className="avatar-sm" alt={person.name} />
				</Link>
			) : (
				<Avatar userId={person.id} className="avatar-sm" alt={person.name} />
			)}
			<div className="employee-info">
				{linkable ? (
					<Link className="employee-name" to={profilePath}>
						{fullName}
					</Link>
				) : (
					<span className="employee-name">{fullName}</span>
				)}
				{date && (
					<Moment className="employee-code" format="D/MM/YYYY">
						{date}
					</Moment>
				)}
			</div>
		</div>
	);
};

const ManagedAbsencesList = (props) => {
	const { t } = useTranslation();

	if (
		props.managed_absences == undefined ||
		props.managed_absences.length === 0
	)
		return <p className="no-results">{t("absences.noResults")}</p>;

	const getDesktopColumns = () => [
		{
			key: "requested_by",
			header: t("common.employee"),
			className: "pending-absence-data-table-employee",
			sortAccessor: (absence) =>
				`${absence.requester.name} ${absence.requester.surname}`,
			render: (absence) => renderTableEmployee(absence.requester),
		},
		{
			key: "period",
			header: t("absences.periodColumn"),
			className: "pending-absence-data-table-date",
			sortAccessor: (absence) => moment(absence.start).valueOf(),
			render: (absence) => getPeriodLabel(absence),
		},
		{
			key: "shift",
			header: t("common.type"),
			className: "pending-absence-data-table-type",
			sortAccessor: (absence) =>
				(absence.shift && absence.shift.name) || absence.shift_type,
			render: (absence) => renderTableType(absence),
		},
		{
			key: "requested",
			header: t("absences.requestedOn"),
			className: "pending-absence-data-table-requested",
			sortAccessor: (absence) => moment(absence.requested).valueOf(),
			render: (absence) => moment(absence.requested).format("DD/MM/YYYY"),
		},
		{
			key: "file",
			header: t("absences.form.attachedDocuments"),
			className: "pending-absence-data-table-file",
			sortable: false,
			render: (absence) => renderTableAttachments(absence),
		},
		{
			key: "status",
			header: t("absences.statusColumn"),
			className: "managed-absence-data-table-status",
			render: (absence) => (
				<span className={`estado absence-status-pill ${getStatusPillClass(absence.status)}`}>
					{absence.status}
				</span>
			),
		},
		{
			key: "managed_by",
			header: t("absences.managedBy"),
			className: "managed-absence-data-table-manager",
			render: (absence) =>
				absence.answerer
					? renderTableEmployee(absence.answerer, absence.answered, false)
					: null,
		},
		{
			key: "answer_comments",
			header: "",
			className: "managed-absence-data-table-comment",
			sortable: false,
			render: (absence) => {
				if (!absence.answer_comments || !absence.answer_comments.trim()) {
					return null;
				}

				return (
					<OverlayTrigger
						trigger="click"
						placement="left"
						rootClose
						overlay={
							<Popover id={`absence-comment-${absence.id}`}>
								<Popover.Body>{absence.answer_comments}</Popover.Body>
							</Popover>
						}
					>
						<button
							type="button"
							className="btn-action-icon btn-action-icon-comment"
							title={t("absences.form.comments")}
						>
							<FiMessageSquare />
						</button>
					</OverlayTrigger>
				);
			},
		},
	];

	const getActions = (absence) => (
		<Dropdown>
			<Dropdown.Toggle variant="action">
				<FiMoreHorizontal />
			</Dropdown.Toggle>
			<Dropdown.Menu>
				<Dropdown.Item
					as="button"
					onClick={() => props.openEditAbsenceModal(absence, true)}
				>
					{t("absences.viewAction")}
				</Dropdown.Item>
				{absence.status !== wfStatus.denied && absence.status !== wfStatus.canceled && (
					<Dropdown.Item
						as="button"
						onClick={() => props.handleCancelRequest(absence)}
					>
						{t("absences.cancelRequest")}
					</Dropdown.Item>
				)}
				{absence.status !== wfStatus.pending && (
					<Dropdown.Item
						as="button"
						onClick={() => props.handleReopenRequest(absence)}
					>
						{t("absences.reopenRequest")}
					</Dropdown.Item>
				)}
			</Dropdown.Menu>
		</Dropdown>
	);

	const downloadAttachment = async (absence, attachment) => {
		const response = await workflowsService.getAttachment(
			absence.id,
			attachment.url
		);
		const url = window.URL.createObjectURL(response);
		const link = document.createElement("a");
		link.href = url;
		link.download = attachment.name;
		document.body.appendChild(link);
		link.click();
		link.remove();
		window.URL.revokeObjectURL(url);
	};

	const renderTableEmployee = (person, date = null, linkable = true) => {
		if (!person) return null;

		const content = (
			<div className="pending-absence-employee">
				<Avatar userId={person.id} className="avatar-sm" alt={person.name} />
				<div>
					<strong>{person.name} {person.surname}</strong>
					<span>
						{date
							? moment(date).format("DD/MM/YYYY")
							: person.position || t("absences.noPosition")}
					</span>
				</div>
			</div>
		);

		return linkable ? (
			<Link className="managed-absence-data-table-employee-link" to={`/employees/${person.id}`}>
				{content}
			</Link>
		) : content;
	};

	const renderTableType = (absence) => {
		const color = shiftPillColor(absence.shift || {}) || "#5a49ff";
		return (
			<div className="pending-absence-type">
				<span
					className="pending-absence-code"
					style={{
						borderColor: color,
						backgroundColor: getTypeBackground(color),
					}}
				>
					{getAbsenceCode(absence)}
				</span>
			</div>
		);
	};

	const renderTableAttachments = (absence) => {
		if (!absence.attachments || absence.attachments.length === 0) {
			return <span>-</span>;
		}

		return (
			<div className="pending-absence-attachments">
				{absence.attachments.map((attachment) => (
					<button
						type="button"
						key={attachment.url}
						onClick={() => downloadAttachment(absence, attachment)}
						title={attachment.name}
					>
						<FiPaperclip aria-hidden="true" />
						<span>{attachment.name}</span>
						{attachment.size ? (
							<small>({Math.round(attachment.size / 1000)}kb)</small>
						) : null}
					</button>
				))}
			</div>
		);
	};

	const getGridRows = () =>
		props.managed_absences.map((absence) => {
			const shiftName =
				(absence.shift && absence.shift.name) || absence.shift_type || "";
			const typeColor = shiftPillColor(absence.shift || {}) || "#5a49ff";

			return (
				<article
					className="pending-absence-card managed-absence-card"
					key={absence.id}
				>
					<header className="pending-absence-card-header">
						<div className="pending-absence-employee">
							<Avatar
								userId={absence.requester.id}
								className="avatar-sm"
								alt={absence.requester.name}
							/>
							<div>
								<strong>
									{absence.requester.name} {absence.requester.surname}
								</strong>
								<span>
									{absence.requester.position || t("absences.noPosition")}
								</span>
							</div>
						</div>
						{getActions(absence)}
					</header>

					<div className="pending-absence-card-body">
						<div className="managed-absence-card-period-row">
							<div className="pending-absence-period">
								<FiCalendar aria-hidden="true" />
								<strong>{getPeriodLabel(absence)}</strong>
							</div>
							<span className={`estado absence-status-pill ${getStatusPillClass(absence.status)}`}>
								{absence.status}
							</span>
						</div>

						<div className="pending-absence-type">
							<span
								className="pending-absence-code"
								style={{
									borderColor: typeColor,
									backgroundColor: getTypeBackground(typeColor),
								}}
							>
								{getAbsenceCode(absence)}
							</span>
							<span className="pending-absence-type-name">{shiftName}</span>
							<span className="pending-absence-type-duration">
								Â· {getDurationLabel(absence)}
							</span>
						</div>

						{absence.answerer && (
							<div className="my-absence-card-manager managed-absence-card-manager">
								<span className="my-absence-card-manager-label">
									{t("absences.managedBy")}
								</span>
								<Avatar
									userId={absence.answerer.id}
									className="avatar-3xs"
									alt={absence.answerer.name}
								/>
								<strong>
									{absence.answerer.name} {absence.answerer.surname}
								</strong>
							</div>
						)}

						{absence.answer_comments && (
							<div className="my-absence-card-answer managed-absence-card-answer">
								<FiMessageSquare aria-hidden="true" />
								<span>{absence.answer_comments}</span>
							</div>
						)}

						{absence.attachments && absence.attachments.length > 0 && (
							<div className="pending-absence-attachments managed-absence-card-attachments">
								{absence.attachments.map((attachment) => (
									<button
										type="button"
										key={attachment.url}
										onClick={() => downloadAttachment(absence, attachment)}
										title={attachment.name}
									>
										<FiPaperclip aria-hidden="true" />
										<span>{attachment.name}</span>
									</button>
								))}
							</div>
						)}

						<div className="managed-absence-card-requested">
							{t("absences.requestedOn")}{" "}
							{moment(absence.requested).format("DD/MM/YYYY")}
						</div>
					</div>
				</article>
			);
		});

	if (props.is_list_view) {
		return (
			<DataTable
				className="pending-absence-data-table-wrap"
				tableClassName="table-absences pending-absence-data-table managed-absence-data-table table-hover"
				columns={getDesktopColumns()}
				rows={props.managed_absences}
				rowKey={(row) => row.id}
				emptyMessage={t("absences.noResults")}
				actionsColumn={{
					header: t("common.actions"),
					render: (absence) => getActions(absence),
					className: "pending-absence-data-table-actions-cell",
				}}
			/>
		);
	}

	return (
		<div className="pending-absence-grid managed-absence-grid">
			{getGridRows()}
		</div>
	);
};

const getEmptyRow = () => {
	return <p className="no-results">Sin resultados.</p>;
};

const getDesktopColumns = () => [
	{
		key: "requested_by",
		header: i18next.t("absences.managed.requestedBy"),
		className: "td-name",
		render: (absence) =>
			renderPersonWithDate({
				person: absence.requester,
				date: absence.requested,
			}),
	},
	{
		key: "shift",
		header: i18next.t("common.shift"),
		render: (absence) => (
			<ShiftPill
				name={absence.shift.name}
				color={shiftPillColor(absence.shift)}
			/>
		),
	},
	{
		key: "period",
		header: i18next.t("common.period"),
		render: (absence) => <AbsenceListItemPeriod absence={absence} />,
	},
	{
		key: "status",
		header: i18next.t("common.status"),
		render: (absence) => (
			<span className={`estado absence-status-pill ${getStatusPillClass(absence.status)}`}>
				{absence.status}
			</span>
		),
	},
	{
		key: "managed_by",
		header: i18next.t("absences.managed.managedBy"),
		className: "td-name",
		render: (absence) =>
			absence.answerer
				? renderPersonWithDate({
						person: absence.answerer,
						date: absence.answered,
					})
				: null,
	},
];

const getTableRowsTablet = (props) =>
	props.managed_absences.map((absence) => (
		<tr key={absence.id} className="row-ausencia">
			<td className="td-name">
				<div className="td-name-content">
					<EmployeeLink employee={absence.requester}>
						<Avatar
							userId={absence.requester.id}
							className="avatar-sm"
							alt={absence.requester.name}
						/>
					</EmployeeLink>
					<div className="employee-info">
						<EmployeeLink
							className="employee-name"
							employee={absence.requester}
						>
							{absence.requester.name} {absence.requester.surname}
						</EmployeeLink>
					</div>
				</div>
			</td>
			<td>
				<ShiftPill
					name={absence.shift.name}
					color={shiftPillColor(absence.shift)}
				/>
			</td>
			<td>
				<span className={`estado absence-status-pill ${getStatusPillClass(absence.status)}`}>
					{absence.status}
				</span>
			</td>
			<td>
				<AbsenceListItemPeriod absence={absence} />
			</td>
			<td>
				{absence.attachments && absence.attachments.length > 0 && (
					<MdAttachFile title={i18next.t("absences.managed.hasAttachments")} className="absence-attachment" />
				)}
			</td>
			<td className="td-actions">{getActions(props, absence)}</td>
		</tr>
	));

const getTableRowsMobile = (props) =>
	props.managed_absences.map((absence) => (
		<tr key={absence.id} className="row-ausencia">
			<td className="td-name">
				<div className="td-name-content">
					<EmployeeLink employee={absence.requester}>
						<Avatar
							userId={absence.requester.id}
							className="avatar-sm"
							alt={absence.requester.name}
						/>
					</EmployeeLink>
					<div className="employee-info">
						<EmployeeLink
							className="employee-name"
							employee={absence.requester}
						>
							{absence.requester.name} {absence.requester.surname}
						</EmployeeLink>
					</div>
				</div>

				<AbsenceListItemPeriod absence={absence} />
			</td>
			<td>
				<ShiftPill
					name={absence.shift.name}
					color={shiftPillColor(absence.shift)}
				/>

				<span className={`estado absence-status-pill ${getStatusPillClass(absence.status)}`}>
					{absence.status}
				</span>
			</td>

			<td className="td-actions">{getActions(props, absence)}</td>
		</tr>
	));

const getGridRows = (props) =>
	props.managed_absences.map((absence) => (
		<div className="col-sm-6 col-lg-4 col-xl-3" key={absence.id}>
			<article className="card card-ausencia" key={absence.id}>
				<EmployeeListItemHeader employee={absence.requester} />

				<div className="card-content">
					{/* <span className="absence-comments">{absence.comments}</span> */}

					<ShiftPill
						name={absence.shift.name}
						color={shiftPillColor(absence.shift)}
					/>

					<AbsenceListItemPeriod absence={absence} />

					<p className="card-label card-label-solicitado">
						{i18next.t("absences.managed.requestedOn")}{" "}
						<Moment className="tag-dia" format="D/MM/YYYY">
							{absence.requested}
						</Moment>
					</p>

					<div className="form-group no-form">
						<span className={`estado absence-status-pill ${getStatusPillClass(absence.status)}`}>
							{absence.status}
						</span>
					</div>

					{absence.answerer && (
						<div className="form-group no-form card-label-gestionado">
							<Avatar
								userId={absence.answerer.id}
								className="avatar-xxs"
								alt={absence.answerer.name}
							/>{" "}
							{absence.answerer.name} {absence.answerer.surname}
						</div>
					)}

					{absence.answer_comments && (
						<span className="absence-answer-comments">
							{absence.answer_comments}
						</span>
					)}
				</div>

				<div className="card-actions bottom-right">
					{getActions(props, absence)}
				</div>
			</article>
		</div>
	));

const getActions = (props, absence) => {
	return (
		<Dropdown>
			<Dropdown.Toggle variant="action">
				<FiMoreHorizontal />
			</Dropdown.Toggle>
			<Dropdown.Menu>
				<Dropdown.Item
					as="button"
					onClick={() => props.openEditAbsenceModal(absence, true)}
				>
					Ver
				</Dropdown.Item>
				{absence.status !== wfStatus.denied && absence.status !== wfStatus.canceled && (
					<Dropdown.Item
						as="button"
						onClick={() => props.handleCancelRequest(absence)}
					>
						Cancelar ausencia
					</Dropdown.Item>
				)}
				{absence.status !== wfStatus.pending && (
					<Dropdown.Item
						as="button"
						onClick={() => props.handleReopenRequest(absence)}
					>
						Reabrir ausencia
					</Dropdown.Item>
				)}
			</Dropdown.Menu>
		</Dropdown>
	);
};

export default ManagedAbsencesList;
